import { useCallback, useRef, useState } from "react";

import type { ComposerSubmitMeta } from "~/components/MessageComposer";
import {
  CHANNEL_TYPE_EVENT,
  getChannelTypeState,
  setChannelType,
  type ChannelType,
} from "~/lib/channel-type";
import { canSendStateEvent, getMembers, getUserId, inviteUser, type Client } from "~/lib/matrix";
import { ownAgentUserId } from "~/lib/own-agent";

export type AgentMentionChoice = "add" | "send";

export interface OwnAgentMentionGate {
  wrap: <A extends [string, string | null, string[], boolean, ComposerSubmitMeta]>(
    send: (...args: A) => void | Promise<void>,
  ) => (...args: A) => Promise<void>;
  pending: { busy: boolean; error: string | null } | null;
  choose: (choice: AgentMentionChoice) => void;
  cancel: () => void;
}

const ADD_FAILED_MESSAGE = "Couldn't add your agent. Try again, or send without it.";

interface PendingSend {
  run: () => void | Promise<void>;
  agentId: string;
  state: ChannelType | null;
  settle: (result: void | Promise<void>) => void;
  abort: () => void;
}

export function useOwnAgentMentionGate(client: Client, roomId: string): OwnAgentMentionGate {
  const [pending, setPending] = useState<{ busy: boolean; error: string | null } | null>(null);
  const pendingRef = useRef<PendingSend | null>(null);

  const wrap: OwnAgentMentionGate["wrap"] =
    (send) =>
    (...args) =>
      new Promise<void>((resolve, reject) => {
        const settle = (result: void | Promise<void>) => {
          try {
            Promise.resolve(result).then(resolve, reject);
          } catch (error) {
            reject(error);
          }
        };
        const abort = () => reject(new DOMException("Send cancelled", "AbortError"));
        const agentId = ownAgentUserId(getUserId(client));
        if (!agentId || !args[2].includes(agentId)) {
          settle(send(...args));
          return;
        }
        if (getMembers(client, roomId).some((m) => m.userId === agentId)) {
          settle(send(...args));
          return;
        }
        getChannelTypeState(client, roomId)
          .then((state) => {
            if (pendingRef.current) {
              abort();
              return;
            }
            if (state === "human-only") {
              settle(send(...args));
              return;
            }
            if (
              state === null &&
              !canSendStateEvent(client, roomId, getUserId(client) ?? "", CHANNEL_TYPE_EVENT)
            ) {
              settle(send(...args));
              return;
            }
            pendingRef.current = {
              run: () => send(...args),
              agentId,
              state,
              settle,
              abort,
            };
            setPending({ busy: false, error: null });
          })
          .catch((error) => reject(error));
      });

  const choose = useCallback(
    (choice: AgentMentionChoice) => {
      const p = pendingRef.current;
      if (!p) return;
      if (choice === "send") {
        pendingRef.current = null;
        setPending(null);
        p.settle(p.run());
        return;
      }
      setPending({ busy: true, error: null });
      (async () => {
        try {
          if (p.state === null) await setChannelType(client, roomId, "mixed");
          await inviteUser(client, roomId, p.agentId);
          if (pendingRef.current !== p) return;
          pendingRef.current = null;
          setPending(null);
          p.settle(p.run());
        } catch {
          if (pendingRef.current !== p) return;
          setPending({ busy: false, error: ADD_FAILED_MESSAGE });
        }
      })();
    },
    [client, roomId],
  );

  const cancel = useCallback(() => {
    const p = pendingRef.current;
    if (!p) return;
    pendingRef.current = null;
    setPending(null);
    p.abort();
  }, []);

  return { wrap, pending, choose, cancel };
}
